import { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './Map.css';
import { Box, CircularProgress } from '@mui/material';
import api, { AreaStats, GlobalStats } from '../../services/api';
// FloatingPanel removed - now handled by VotingScreen
import { getEmotionIconByPercentage, getColorForPercentage, getNoVotesEmotionIcon, NO_VOTES_COLOR } from '../../utils/emotionUtils';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import { useTranslation } from '../../hooks/useTranslation';
import instagramIcon from '../../assets/icons/instagram.svg';
import tiktokIcon from '../../assets/icons/tik-tok.svg';
import blueskyIcon from '../../assets/icons/bluesky.svg';

// Helper functions for color manipulation
const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

const rgbToHex = (r: number, g: number, b: number) => {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const darkenColor = (hex: string, amount: number) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const darken = (c: number) => Math.max(0, Math.floor(c * (1 - amount)));
  return rgbToHex(darken(rgb.r), darken(rgb.g), darken(rgb.b));
};

// Configurar el token de acceso de Mapbox
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || '';

// Definir los IDs de los tilesets y sus source-layers
const LAYER_CONFIG = {
  city: {
    tilesetId: 'mapbox://heavyfish.6yjv4mos', // Usamos el mismo tileset que boroughs
    sourceLayer: 'boroughs-1nam6i'
  },
  boroughs: {
    tilesetId: 'mapbox://heavyfish.6yjv4mos',
    sourceLayer: 'boroughs-1nam6i'
  },
  neighborhoods: {
    tilesetId: 'mapbox://heavyfish.68kmhfsc',
    sourceLayer: 'neighborhoods-bf9mcx'
  }
};

// Definir los niveles de zoom para cada capa
const ZOOM_LEVELS = {
  city: { min: 0, max: 9.5 },
  boroughs: { min: 9.5, max: 11.5 },
  neighborhoods: { min: 11.5, max: 22 }
};

// Constantes para el tamaño de los emoticones por nivel
const ICON_SIZES = {
  city: {
    min: 96,
    max: 128
  },
  boroughs: {
    min: 32,
    max: 80
  },
  neighborhoods: {
    min: 24,
    max: 56
  }
};

const enhanceBaseMap = (map: mapboxgl.Map) => {
  if (!map) return;
  
  const style = map.getStyle();
  if (!style || !style.layers) return;
  
  const layers = style.layers;

  // Layer IDs from our app to ignore
  const appLayerIds = new Set(Object.keys(LAYER_CONFIG).flatMap(l => [`${l}-fill`, `${l}-outline`]));

  layers.forEach(layer => {
    if (appLayerIds.has(layer.id)) return; // Don't modify our own layers

    // Remove borders from administrative boundaries and similar layers
    if (layer.type === 'line' && (
      layer.id.includes('admin') || 
      layer.id.includes('boundary') || 
      layer.id.includes('border') ||
      layer.id.includes('place') ||
      layer.id.includes('state') ||
      layer.id.includes('country') ||
      layer.id.includes('region')
    )) {
      try {
        map.setPaintProperty(layer.id, 'line-opacity', 0);
        map.setLayoutProperty(layer.id, 'visibility', 'none');
      } catch(e) { /* ignore */ }
    }

    // Configure all water layers to be dark
    if (layer.type === 'fill' && (layer.id.includes('water') || layer.id.includes('ocean') || layer.id.includes('sea'))) {
      try {
        map.setPaintProperty(layer.id, 'fill-color', '#141414');
        map.setPaintProperty(layer.id, 'fill-opacity', 1);
      } catch(e) { /* ignore */ }
    }

    // Enhance road visibility with differential styling for dark theme
    if (layer.type === 'line' && (
      layer.id.includes('road') || 
      layer.id.includes('street') || 
      layer.id.includes('bridge') ||
      layer.id.includes('tunnel') ||
      layer.id.includes('path') ||
      layer.id.includes('highway') ||
      layer.id.includes('local') ||
      layer.id.includes('minor')
    )) {
      const layerId = layer.id.toLowerCase();
      
      // Major roads - clearly visible
      if (layerId.includes('primary') || layerId.includes('motorway') || layerId.includes('trunk') || layerId.includes('highway')) {
        try {
          map.setPaintProperty(layer.id, 'line-color', '#808080'); // Más claro y visible
          map.setPaintProperty(layer.id, 'line-opacity', 0.4);
          map.setPaintProperty(layer.id, 'line-width', 1.2);
        } catch(e) { /* ignore */ }
      } else { // Secondary roads - moderately visible
        try {
          map.setPaintProperty(layer.id, 'line-color', '#606060'); // Más claro que antes
          map.setPaintProperty(layer.id, 'line-opacity', 0.4);
          map.setPaintProperty(layer.id, 'line-width', 0.8);
        } catch(e) { /* ignore */ }
      }
    }

    // Enhance label visibility for dark theme and control street name visibility by zoom
    if (layer.type === 'symbol') {
      try {
        const layerId = layer.id.toLowerCase();
        
        // Control street name visibility - only show at zoom 15+
        if (layerId.includes('road') || layerId.includes('street') || layerId.includes('highway') || 
            layerId.includes('local') || layerId.includes('minor') || layerId.includes('path')) {
          // Set text opacity to 0 for zoom levels below 15
          map.setPaintProperty(layer.id, 'text-opacity', [
            'interpolate',
            ['linear'],
            ['zoom'],
            14.9, 0,    // Completely hidden below zoom 15
            15, 1       // Fully visible at zoom 15 and above
          ]);
        }
        
        // Identificar y mejorar etiquetas de nombres de áreas/barrios
        if (layerId.includes('place') || layerId.includes('neighborhood') || 
            layerId.includes('district') || layerId.includes('area') ||
            (layerId.includes('label') && !layerId.includes('road'))) {
          // Estas son etiquetas de nombres de áreas - solo texto blanco sin halo
          map.setPaintProperty(layer.id, 'text-color', '#ffffff'); // Texto blanco
          map.setPaintProperty(layer.id, 'text-halo-width', 0); // Sin halo
        } else {
          // Para otras etiquetas (calles, etc.)
          map.setPaintProperty(layer.id, 'text-color', '#e2e8f0'); // Texto claro
          map.setPaintProperty(layer.id, 'text-halo-color', 'rgba(26, 26, 26, 0.8)'); // Halo oscuro
          map.setPaintProperty(layer.id, 'text-halo-width', 2);
          map.setPaintProperty(layer.id, 'text-halo-blur', 1);
        }
      } catch(e) { /* ignore errors for layers that don't have this property */}
    }
  });
};

// Función para mover las etiquetas de nombres de áreas por encima de nuestras capas
const moveAreaLabelsAboveOurLayers = (map: mapboxgl.Map) => {
  if (!map) return;
  
  try {
    const style = map.getStyle();
    if (!style || !style.layers) return;
    
    const layers = style.layers;
    const appLayerIds = new Set(Object.keys(LAYER_CONFIG).flatMap(l => [`${l}-fill`, `${l}-outline`]));
    
    // Encontrar la última capa de nuestra aplicación
    let lastAppLayerIndex = -1;
    layers.forEach((layer, index) => {
      if (appLayerIds.has(layer.id)) {
        lastAppLayerIndex = Math.max(lastAppLayerIndex, index);
      }
    });
    
    if (lastAppLayerIndex === -1) return;
    
    // Identificar y mover las capas de texto de nombres de áreas
    const areaLabelLayers: string[] = [];
    
    layers.forEach(layer => {
      if (layer.type === 'symbol') {
        const layerId = layer.id.toLowerCase();
        // Identificar capas de nombres de áreas/barrios
        if (layerId.includes('place') || layerId.includes('neighborhood') || 
            layerId.includes('district') || layerId.includes('area') ||
            (layerId.includes('label') && !layerId.includes('road'))) {
          areaLabelLayers.push(layer.id);
        }
      }
    });
    
    // Mover cada capa de etiqueta de área por encima de nuestras capas
    areaLabelLayers.forEach(layerId => {
      try {
        if (map.getLayer(layerId)) {
          // Mover la capa justo después de nuestra última capa
          map.moveLayer(layerId, layers[lastAppLayerIndex].id);
        }
      } catch(e) {
        console.warn(`No se pudo mover la capa ${layerId}:`, e);
      }
    });
    
  } catch(e) {
    console.warn('Error moviendo etiquetas de áreas:', e);
  }
};

interface MapProps {
  initialLocation?: { latitude: number; longitude: number } | null;
  initialNeighborhoodId?: string | null;
  isOutsideNYCView?: boolean;
}

const Map = ({
  initialLocation = null,
  initialNeighborhoodId = null,
  isOutsideNYCView = false
}: MapProps) => {
  const { t, currentLanguage } = useTranslation();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const popup = useRef<mapboxgl.Popup | null>(null);
  const zoomTimeout = useRef<NodeJS.Timeout>();
  const [selectedArea, setSelectedArea] = useState<AreaStats | null>(null);
  const [areaData, setAreaData] = useState<{ [key: string]: AreaStats }>({});
  const areaDataRef = useRef<{ [key: string]: AreaStats }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
    neighborhoodId: string | null;
    isInNYC: boolean;
  } | null>(null);
  const userLocationRef = useRef<typeof userLocation>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [globalStats, setGlobalStats] = useState<GlobalStats>({
    total_areas: 0,
    total_votes: 0,
    positive_votes: 0,
    last_map_update: new Date().toISOString()
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [lastVote, setLastVote] = useState<{
    isPositive: boolean;
    timestamp: string;
    neighborhood: string;
    color: string;
  } | null>(null);

  const isMobileViewport = useCallback(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 1024 || 'ontouchstart' in window;
  }, []);

  const getAreaPopupOptions = useCallback((): mapboxgl.PopupOptions => {
    const isMobile = isMobileViewport();
    return {
      closeButton: isMobile,
      closeOnClick: false,
      maxWidth: isMobile ? '260px' : 'none',
      className: 'area-tooltip',
      ...(isMobile
        ? {
            // En mobile mantenemos el tooltip con la punta abajo y centrada.
            anchor: 'bottom' as const,
            offset: [0, -8] as [number, number]
          }
        : {})
    };
  }, [isMobileViewport]);

  // Añadir una referencia para el caché de estilos
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const styleCache = useRef<{
    [key: string]: {
      features: string[];
      style: any;
    }
  }>({});

  // Referencia para los marcadores de emoticones
  const emotionMarkers = useRef<{ [key: string]: mapboxgl.Marker }>({});
  const emotionMarkerMeta = useRef<{ [key: string]: { level: string; signature: string } }>({});
  const layerStyleSignatureRef = useRef<{ [key: string]: string }>({});
  // Añadir referencia para las etiquetas
  const labelMarkers = useRef<{ [key: string]: mapboxgl.Marker }>({});

  // Referencia para controlar el estado de carga
  const dataLoadingRef = useRef(false);
  const mapInitializedRef = useRef(false);
  const lastAreaDataSignatureRef = useRef<string>('');
  const mapIntegrityRafRef = useRef<number | null>(null);

  const buildAreaDataSignature = useCallback((dataMap: { [key: string]: AreaStats }) => {
    return Object.values(dataMap)
      .sort((a, b) => a.id.localeCompare(b.id))
      // Usar únicamente conteos para evitar cambios espurios por floats/confidence.
      .map(area => `${area.id}:${area.total_votes}:${area.positive_votes}:${area.negative_votes}`)
      .join('|');
  }, []);

  // Función para calcular el tamaño del emoticón basado en el área, zoom y nivel
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const calculateIconSize = useCallback((features: any[], zoom: number, level: string) => {
    const sizeConfig = ICON_SIZES[level as keyof typeof ICON_SIZES];
    if (!sizeConfig || sizeConfig.max === 0) return 0;

    // Para boroughs, usar un tamaño más fijo basado principalmente en el zoom
    if (level === 'boroughs') {
      const zoomFactor = Math.pow(1.15, zoom - 10);
      return Math.min(Math.max(sizeConfig.min * zoomFactor, sizeConfig.min), sizeConfig.max);
    }

    // Para otros niveles, mantener el cálculo basado en área
    const bounds = new mapboxgl.LngLatBounds();
    features.forEach(feature => {
      if (!feature.geometry) return;
      const coordinates = feature.geometry.coordinates[0];
      coordinates.forEach((coord: [number, number]) => {
        if (isValidCoordinate(coord[0], coord[1])) {
          bounds.extend(coord as mapboxgl.LngLatLike);
        }
      });
    });
    
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const width = Math.abs(ne.lng - sw.lng);
    const height = Math.abs(ne.lat - sw.lat);
    const area = width * height;
    
    const zoomFactor = Math.pow(1.15, zoom - 10);
    const baseSize = sizeConfig.min + (Math.log10(1 + area * 10000) * (sizeConfig.max - sizeConfig.min) / 2);
    const size = baseSize * zoomFactor;
    
    return Math.min(Math.max(size, sizeConfig.min), sizeConfig.max);
  }, []);

  // Función para obtener el emoticón correspondiente al porcentaje de votos positivos
  const getEmotionIcon = useCallback((positivePercentage: number, totalVotes: number = 1) => {
    if (totalVotes <= 0) {
      return getNoVotesEmotionIcon();
    }
    return getEmotionIconByPercentage(positivePercentage);
  }, []);

  // Función para agrupar features adyacentes del mismo color
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const groupAdjacentFeatures = useCallback((features: any[], areaData: { [key: string]: AreaStats }) => {
    const groups: { [key: string]: any[] } = {};
    const processed = new Set<string>();

    const distance = (p1: number[], p2: number[]) => {
      return Math.sqrt(Math.pow(p1[0] - p2[0], 2) + Math.pow(p1[1] - p2[1], 2));
    };

    features.forEach(feature => {
      const areaId = feature.properties?.id;
      if (processed.has(areaId)) return;

      const areaStats = areaId ? areaData[areaId] : null;
      if (!areaStats || !areaStats.total_votes) return;

      const color = areaStats.color;
      const group: any[] = [feature];
      processed.add(areaId);

      // Buscar áreas adyacentes del mismo color
      let foundNew = true;
      while (foundNew) {
        foundNew = false;
        features.forEach(otherFeature => { // eslint-disable-line no-loop-func
          const otherId = otherFeature.properties?.id;
          if (processed.has(otherId)) return;

          const otherStats = otherId ? areaData[otherId] : null;
          if (!otherStats || !otherStats.total_votes || otherStats.color !== color) return;

          // Verificar si es adyacente a algún elemento del grupo
          const isAdjacent = group.some(groupFeature => {
            const groupCoords = groupFeature.geometry.coordinates[0];
            const otherCoords = otherFeature.geometry.coordinates[0];
            
            // Buscar puntos cercanos entre los polígonos
            return groupCoords.some((coord1: number[]) => 
              otherCoords.some((coord2: number[]) => {
                const dist = distance(coord1, coord2);
                return dist < 0.0002; // Umbral aumentado para mejor detección
              })
            );
          });

          if (isAdjacent) {
            group.push(otherFeature);
            processed.add(otherId);
            foundNew = true;
          }
        });
      }

      // Crear un ID único para el grupo
      const groupId = group.map(f => f.properties.id).sort().join('-');
      groups[groupId] = group;
    });

    return groups;
  }, []);

  // Función auxiliar para calcular el centro geométrico
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const calculateGeometricCenter = (geometry: any) => {
    if (!geometry || !geometry.coordinates) return null;

    // Función para calcular el área de un polígono
    const calculatePolygonArea = (coords: number[][]) => {
      let area = 0;
      for (let i = 0; i < coords.length - 1; i++) {
        area += coords[i][0] * coords[i + 1][1] - coords[i + 1][0] * coords[i][1];
      }
      return Math.abs(area / 2);
    };

    // Función para calcular el centro de un polígono individual
    const calculatePolygonCenter = (coords: number[][]) => {
      let cx = 0, cy = 0;
      const area = calculatePolygonArea(coords);
      
      if (area === 0) return null;

      for (let i = 0; i < coords.length - 1; i++) {
        const factor = (coords[i][0] * coords[i + 1][1] - coords[i + 1][0] * coords[i][1]);
        cx += (coords[i][0] + coords[i + 1][0]) * factor;
        cy += (coords[i][1] + coords[i + 1][1]) * factor;
      }

      const f = 1 / (6 * area);
      return {
        x: cx * f,
        y: cy * f
      };
    };

    try {
      let totalArea = 0;
      let weightedX = 0;
      let weightedY = 0;

      // Procesar MultiPolygon
      if (geometry.type === 'MultiPolygon') {
        geometry.coordinates.forEach((polygon: number[][][]) => {
          polygon.forEach((ring: number[][]) => {
            const center = calculatePolygonCenter(ring);
            const area = calculatePolygonArea(ring);
            if (center && isValidCoordinate(center.x, center.y)) {
              totalArea += area;
              weightedX += center.x * area;
              weightedY += center.y * area;
            }
          });
        });
      }
      // Procesar Polygon
      else if (geometry.type === 'Polygon') {
        geometry.coordinates.forEach((ring: number[][]) => {
          const center = calculatePolygonCenter(ring);
          const area = calculatePolygonArea(ring);
          if (center && isValidCoordinate(center.x, center.y)) {
            totalArea += area;
            weightedX += center.x * area;
            weightedY += center.y * area;
          }
        });
      }

      if (totalArea === 0) return null;

      return {
        x: weightedX / totalArea,
        y: weightedY / totalArea
      };
    } catch (error) {
      console.warn('Error procesando geometría:', error);
      return null;
    }
  };

  // Función para obtener estadísticas agregadas de toda la ciudad
  const getCityStats = useCallback(() => {
    const currentAreaData = areaDataRef.current;
    // Filtrar solo los datos de los boroughs
    const boroughsData = Object.values(currentAreaData).filter(
      area => area.area_type === 'borough'
    );
    
    if (boroughsData.length === 0) return null;
    
    // Calcular estadísticas agregadas
    const totalVotes = boroughsData.reduce((sum, area) => sum + area.total_votes, 0);
    const positiveVotes = boroughsData.reduce((sum, area) => sum + area.positive_votes, 0);
    const negativeVotes = boroughsData.reduce((sum, area) => sum + area.negative_votes, 0);
    
    // Calcular porcentaje de votos positivos
    const positivePercentage = totalVotes > 0 ? (positiveVotes / totalVotes) * 100 : 50;
    
    // Usar la nueva función para determinar el color
    const color = totalVotes > 0 ? getColorForPercentage(positivePercentage) : NO_VOTES_COLOR;
    
    // Crear un objeto con las estadísticas de la ciudad
    return {
      id: 'nyc',
      name: 'New York City',
      area_type: 'city',
      parent_id: null,
      population: boroughsData.reduce((sum, area) => sum + area.population, 0),
      geometry: null, // No necesitamos la geometría para este caso
      total_votes: totalVotes,
      positive_votes: positiveVotes,
      negative_votes: negativeVotes,
      color: color,
      opacity: 0.75,
      confidence_percentage: 100 // Alta confianza ya que es un agregado
    };
  }, []);

  // Función para actualizar los emoticones en el mapa
  const updateEmotionIcons = useCallback((level: string) => {
    const currentAreaData = areaDataRef.current;
    if (!map.current || !currentAreaData) return;

    const currentZoom = map.current.getZoom();
    const nextMarkerIds = new Set<string>();

    let areasForLevel: (AreaStats & { center?: { lng: number, lat: number } })[] = [];

    if (level === 'city') {
      const cityStats = getCityStats();
      if (cityStats) {
        areasForLevel = [{
          ...cityStats,
          // Usar un ID único para el marcador de la ciudad para evitar conflictos
          id: 'nyc-marker', 
          center: { lng: -74.0060, lat: 40.7128 } // Centro de NYC
        }];
      }
    } else {
      // Filtrar las áreas que corresponden al nivel de zoom actual
      const singleLevel = level.endsWith('s') ? level.slice(0, -1) : level;
      areasForLevel = Object.values(currentAreaData).filter(
        area => area.area_type === singleLevel
      );
    }

    // Iterar sobre los datos y crear los marcadores
    areasForLevel.forEach(area => {
      // Comprobación de seguridad para el centroide
      if (!area.center || typeof area.center.lng !== 'number' || typeof area.center.lat !== 'number') {
        return; // No dibujar emoticón si no hay datos de centro válidos
      }

      const positivePercentage = area.total_votes > 0 ? (area.positive_votes / area.total_votes) * 100 : 50;
      const iconSvg = getEmotionIcon(positivePercentage, area.total_votes);
      
      if (iconSvg) {
        const container = document.createElement('div');
        container.className = 'emotion-marker-container';

        const iconEl = document.createElement('div');
        iconEl.className = 'emotion-marker';
        iconEl.style.backgroundImage = `url(${iconSvg})`;
        
        const sizeConfig = ICON_SIZES[level as keyof typeof ICON_SIZES];
        let size = sizeConfig.min;

        if (level === 'boroughs') {
            const zoomRange = ZOOM_LEVELS.boroughs.max - ZOOM_LEVELS.boroughs.min;
            const zoomProgress = (currentZoom - ZOOM_LEVELS.boroughs.min) / zoomRange;
            size = sizeConfig.min + (sizeConfig.max - sizeConfig.min) * zoomProgress;
        } else if (level === 'neighborhoods') {
            const zoomRange = ZOOM_LEVELS.neighborhoods.max - ZOOM_LEVELS.neighborhoods.min;
            const zoomProgress = (currentZoom - ZOOM_LEVELS.neighborhoods.min) / zoomRange;
            size = sizeConfig.min + (sizeConfig.max - sizeConfig.min) * zoomProgress;
        }
        
        size = Math.max(sizeConfig.min, Math.min(size, sizeConfig.max));

        iconEl.style.width = `${size}px`;
        iconEl.style.height = `${size}px`;

        const labelEl = document.createElement('div');
        labelEl.className = 'emotion-label';
        labelEl.innerText = area.name;
        labelEl.style.fontSize = `${Math.max(10, size / 4)}px`;

        // Añadir borde de color al texto para mejorar la legibilidad
        // Ocultar las etiquetas de nombres de barrios
        // const areaColor = getColorForPercentage(positivePercentage);
        // labelEl.style.color = '#000000';
        
        // // Usamos una sombra de texto para crear un borde, más grueso para la vista de ciudad.
        // let textShadow;
        // if (level === 'city') {
        //   // Borde de ~2px para la vista de ciudad
        //   textShadow = `
        //     -2px -2px 0 ${areaColor}, 2px -2px 0 ${areaColor}, -2px 2px 0 ${areaColor}, 2px 2px 0 ${areaColor},
        //     -2px 0 0 ${areaColor},   2px 0 0 ${areaColor},   0 -2px 0 ${areaColor},   0 2px 0 ${areaColor}
        //   `;
        // } else {
        //   // Borde de ~1px para otras vistas
        //   textShadow = `
        //     -1px -1px 0 ${areaColor},
        //      1px -1px 0 ${areaColor},
        //     -1px  1px 0 ${areaColor},
        //      1px  1px 0 ${areaColor}
        //   `;
        // }
        // labelEl.style.textShadow = textShadow;

        container.appendChild(iconEl);
        // container.appendChild(labelEl); // Comentado para ocultar etiquetas

        const { lng, lat } = area.center;

        // Comprobación final de que las coordenadas son válidas
        if (isNaN(lng) || isNaN(lat)) return;

        const markerId = area.id;
        nextMarkerIds.add(markerId);
        const markerSignature = `${level}:${markerId}:${area.positive_votes}:${area.total_votes}:${lng.toFixed(5)}:${lat.toFixed(5)}:${currentZoom.toFixed(2)}`;
        const existingMeta = emotionMarkerMeta.current[markerId];
        if (
          existingMeta &&
          existingMeta.level === level &&
          existingMeta.signature === markerSignature &&
          emotionMarkers.current[markerId]
        ) {
          return;
        }

        if (emotionMarkers.current[markerId]) {
          emotionMarkers.current[markerId].remove();
          delete emotionMarkers.current[markerId];
          delete emotionMarkerMeta.current[markerId];
        }

        const marker = new mapboxgl.Marker(container)
          .setLngLat([lng, lat])
          .addTo(map.current!);
        
        emotionMarkers.current[markerId] = marker;
        emotionMarkerMeta.current[markerId] = { level, signature: markerSignature };
      }
    });

    // Eliminar marcadores que ya no aplican para este nivel/estado.
    Object.keys(emotionMarkers.current).forEach(markerId => {
      if (!nextMarkerIds.has(markerId)) {
        emotionMarkers.current[markerId].remove();
        delete emotionMarkers.current[markerId];
        delete emotionMarkerMeta.current[markerId];
      }
    });
  }, [getEmotionIcon, getCityStats]);

  // Función para inicializar las capas del mapa
  const initializeLayers = useCallback(() => {
    if (!map.current) return;

    try {
      // Añadir fuentes
      Object.entries(LAYER_CONFIG).forEach(([layer, config]) => {
        const sourceId = `${layer}-source`;
        
        if (!map.current?.getSource(sourceId)) {
          try {
            map.current?.addSource(sourceId, {
              type: 'vector',
              url: config.tilesetId
            });
          } catch (error) {
            console.error(`Error al añadir fuente ${sourceId}:`, error);
          }
        }
      });

      // Añadir capas de relleno
      Object.entries(LAYER_CONFIG).forEach(([layer, config]) => {
        const fillLayerId = `${layer}-fill`;
        
        if (!map.current?.getLayer(fillLayerId)) {
          try {
            map.current?.addLayer({
              id: fillLayerId,
              type: 'fill',
              source: `${layer}-source`,
              'source-layer': config.sourceLayer,
              paint: {
                'fill-color': NO_VOTES_COLOR,
                'fill-opacity': 0.7
              },
              layout: {
                'visibility': 'none'
              },
              ...(layer !== 'city' && { filter: ['has', 'id'] })
            });
          } catch (error) {
            console.error(`Error al añadir capa ${fillLayerId}:`, error);
          }
        }
      });

      // Añadir capas de bordes
      Object.entries(LAYER_CONFIG).forEach(([layer, config]) => {
        const outlineLayerId = `${layer}-outline`;
        
        if (!map.current?.getLayer(outlineLayerId)) {
          try {
            map.current?.addLayer({
              id: outlineLayerId,
              type: 'line',
              source: `${layer}-source`,
              'source-layer': config.sourceLayer,
              paint: {
                'line-color': '#545454',
                'line-width': 0, // Eliminar grosor
                'line-opacity': 0 // Hacer completamente transparente
              },
              layout: {
                'visibility': 'none' // Mantener oculto
              }
            });
          } catch (error) {
            console.error(`Error al añadir capa ${outlineLayerId}:`, error);
          }
        }
      });
    } catch (error) {
      console.error('Error durante la inicialización de capas:', error);
    }
  }, []);

  // Función para actualizar los colores y opacidades de una capa
  const updateLayerStyle = useCallback((layer: string) => {
    const currentAreaData = areaDataRef.current;
    if (!map.current) {
      return;
    }
    
    const fillLayer = `${layer}-fill`;
    const outlineLayer = `${layer}-outline`;

    if (!map.current.getLayer(fillLayer)) {
      return;
    }

    try {
      // Caso especial para la capa de ciudad (es una sola área)
      if (layer === 'city') {
        const cityStats = getCityStats();
        const color = cityStats ? cityStats.color : NO_VOTES_COLOR;
        const styleSignature = cityStats
          ? `city:${cityStats.total_votes}:${cityStats.positive_votes}:${color}`
          : `city:0:0:${NO_VOTES_COLOR}`;
        if (layerStyleSignatureRef.current[layer] === styleSignature) {
          const visibility = map.current.getLayoutProperty(fillLayer, 'visibility');
          if (visibility === 'visible' && cityStats) {
            updateEmotionIcons(layer);
          }
          return;
        }
        layerStyleSignatureRef.current[layer] = styleSignature;
        
        map.current.setPaintProperty(fillLayer, 'fill-color', color);
        map.current.setPaintProperty(fillLayer, 'fill-opacity', 0.7);
        map.current.setPaintProperty(outlineLayer, 'line-color', '#dddddd');
        map.current.setPaintProperty(outlineLayer, 'line-width', 1);
        map.current.setPaintProperty(outlineLayer, 'line-opacity', 0.1);
        
        if (cityStats) {
          updateEmotionIcons(layer);
        }
        return;
      }

      // Para otras capas (boroughs, neighborhoods)
      const currentData = Object.entries(currentAreaData).filter(([_, area]) => {
        const singleLevel = layer.endsWith('s') ? layer.slice(0, -1) : layer;
        return area.area_type === singleLevel;
      });

      const matchPairs: [string, string][] = currentData.map(([id, area]) => {
        const color = area.total_votes > 0
          ? getColorForPercentage((area.positive_votes / area.total_votes) * 100)
          : NO_VOTES_COLOR;
        return [id, color];
      });
      matchPairs.sort(([idA], [idB]) => idA.localeCompare(idB));
      const styleSignature = matchPairs.map(([id, color]) => `${id}:${color}`).join('|');
      if (layerStyleSignatureRef.current[layer] === styleSignature) {
        const visibility = map.current.getLayoutProperty(fillLayer, 'visibility');
        if (visibility === 'visible' && layer !== 'block_groups') {
          updateEmotionIcons(layer);
        }
        return;
      }
      layerStyleSignatureRef.current[layer] = styleSignature;

      const fillColor = matchPairs.length > 0 ? [
        'match',
        ['get', 'id'],
        ...matchPairs.flatMap(([id, color]) => [id, color]),
        NO_VOTES_COLOR
      ] : NO_VOTES_COLOR;

      // Solo usar match si hay datos de votos, sino usar color fijo
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const _outlineColor = matchPairs.length > 0 ? [
        'match',
        ['get', 'id'],
        ...matchPairs.flatMap(([id, color]) => [id, color]),
        '#545454' // Default border color for areas with no votes or default
      ] : '#545454';

      map.current.setPaintProperty(fillLayer, 'fill-color', fillColor);
      map.current.setPaintProperty(fillLayer, 'fill-opacity', 0.7);
      map.current.setPaintProperty(outlineLayer, 'line-color', '#dddddd');
      map.current.setPaintProperty(outlineLayer, 'line-width', 1);
      map.current.setPaintProperty(outlineLayer, 'line-opacity', 0.1);

      const visibility = map.current.getLayoutProperty(fillLayer, 'visibility');
      if (visibility === 'visible' && layer !== 'block_groups') {
        setTimeout(() => {
          updateEmotionIcons(layer);
        }, 100);
      }
    } catch (error) {
      console.error(`Error al actualizar estilos para ${layer}:`, error);
    }
  }, [updateEmotionIcons, getCityStats]);

  // Función para actualizar la visibilidad de las capas según el zoom
  const updateLayerVisibility = useCallback((zoom: number) => {
    if (!map.current) return;

    try {
      Object.entries(ZOOM_LEVELS).forEach(([layer, { min, max }]) => {
        const visible = zoom >= min && zoom <= max;
        const fillLayer = `${layer}-fill`;
        const outlineLayer = `${layer}-outline`;
        
        const fillLayerExists = map.current?.getLayer(fillLayer);
        const outlineLayerExists = map.current?.getLayer(outlineLayer);
        
        if (fillLayerExists && outlineLayerExists) {
          try {
            const currentVisibility = map.current?.getLayoutProperty(fillLayer, 'visibility');
            const shouldUpdate = currentVisibility !== (visible ? 'visible' : 'none');
            
            if (shouldUpdate) {
              if (visible) {
                map.current?.setLayoutProperty(fillLayer, 'visibility', 'visible');
                map.current?.setLayoutProperty(outlineLayer, 'visibility', 'visible');
                updateLayerStyle(layer);
              } else {
                map.current?.setLayoutProperty(fillLayer, 'visibility', 'none');
                map.current?.setLayoutProperty(outlineLayer, 'visibility', 'none');
                
                // Limpiar marcadores
                Object.values(emotionMarkers.current).forEach(marker => marker.remove());
                emotionMarkers.current = {};
                emotionMarkerMeta.current = {};
              }
            }
          } catch (error) {
            console.error(`Error al actualizar visibilidad de ${layer}:`, error);
          }
        }
      });
    } catch (error) {
      console.error('Error durante la actualización de visibilidad:', error);
    }
  }, [updateLayerStyle]);

  // Reasegura fuentes/capas y re-aplica estilos visibles sin reinstanciar Mapbox.
  const ensureMapLayersAndStyles = useCallback(() => {
    if (!map.current || !map.current.isStyleLoaded()) return;

    initializeLayers();
    moveAreaLabelsAboveOurLayers(map.current);

    const zoom = map.current.getZoom();
    updateLayerVisibility(zoom);

    Object.entries(ZOOM_LEVELS).forEach(([layer, { min, max }]) => {
      if (zoom >= min && zoom <= max) {
        updateLayerStyle(layer);
      }
    });
  }, [initializeLayers, updateLayerVisibility, updateLayerStyle]);

  // Función para manejar el cambio de zoom
  const handleZoom = useCallback(() => {
    if (!map.current) return;
    
    const zoom = map.current.getZoom();
    
    // Limpiar el timeout anterior si existe
    if (zoomTimeout.current) {
      clearTimeout(zoomTimeout.current);
    }
    
    // Establecer un nuevo timeout para actualizar la visibilidad
    zoomTimeout.current = setTimeout(() => {
      updateLayerVisibility(zoom);
      
      // Actualizar los emoticones solo para la capa visible
      let visibleLayer = '';
      if (zoom >= ZOOM_LEVELS.neighborhoods.min) {
        visibleLayer = 'neighborhoods';
      } else if (zoom >= ZOOM_LEVELS.boroughs.min) {
        visibleLayer = 'boroughs';
      } else {
        visibleLayer = 'city';
      }
      
      // Actualizar los emoticones para la capa visible
      if (visibleLayer === 'city' || visibleLayer === 'boroughs' || visibleLayer === 'neighborhoods') {
        updateEmotionIcons(visibleLayer);
      }
    }, 300);
  }, [updateLayerVisibility, updateEmotionIcons]);

  // Función para cargar los datos de todas las áreas
  const loadAllAreasData = useCallback(async () => {
    if (dataLoadingRef.current) return;

    try {
      dataLoadingRef.current = true;
      const [data, stats] = await Promise.all([
        api.getAllAreasStats(),
        api.getGlobalStats()
      ]);
      
      if (!Array.isArray(data)) {
        console.error('Los datos recibidos no son un array:', data);
        return;
      }

      // Cuando la pestaña vuelve de background/sleep puede haber errores transitorios
      // de red. No debemos interpretar un array vacío como "no hay áreas", porque el
      // backend siempre devuelve áreas (aunque tengan 0 votos). Evitamos vaciar el mapa.
      if (data.length === 0) {
        console.warn('Se recibió array vacío de áreas; se mantiene el estado previo del mapa.');
        return;
      }

      const dataMap = data.reduce((acc: { [key: string]: AreaStats }, area: AreaStats) => {
        if (!area.id) return acc;
        acc[area.id] = {
          ...area,
          color: area.color || '#E1D4C3'
        };
        return acc;
      }, {});

      const nextSignature = buildAreaDataSignature(dataMap);
      const hasAreaChanges = nextSignature !== lastAreaDataSignatureRef.current;
      setGlobalStats(stats);

      // Si no hay cambios de datos, aún debemos reparar/repintar capas si el estilo de
      // Mapbox se reconstruyó al volver de background.
      if (!hasAreaChanges) {
        ensureMapLayersAndStyles();
        return;
      }

      lastAreaDataSignatureRef.current = nextSignature;
      areaDataRef.current = dataMap;
      setAreaData(dataMap);

      await new Promise(resolve => setTimeout(resolve, 100));

      if (!map.current?.loaded()) {
        await new Promise(resolve => {
          const checkMap = () => {
            if (map.current?.loaded()) {
              resolve(true);
            } else {
              setTimeout(checkMap, 100);
            }
          };
          checkMap();
        });
      }

      ensureMapLayersAndStyles();

    } catch (error) {
      console.error('Error al cargar los datos de las áreas:', error);
    } finally {
      dataLoadingRef.current = false;
    }
  }, [buildAreaDataSignature, ensureMapLayersAndStyles]);

  // Función para verificar si una coordenada está en NYC (aproximadamente)
  const isInNYC = useCallback((lat: number, lng: number) => {
    const nycBounds = {
      north: 40.917577,
      south: 40.477399,
      east: -73.700272,
      west: -74.259090
    };
    return lat >= nycBounds.south && lat <= nycBounds.north && 
           lng >= nycBounds.west && lng <= nycBounds.east;
  }, []);

  // Función para verificar si el centro del mapa está en NYC
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isMapCenterInNYC = useCallback(() => {
    if (!map.current) return true;
    const center = map.current.getCenter();
    return isInNYC(center.lat, center.lng);
  }, [isInNYC]);

  // Función para obtener el neighborhood ID basado en la ubicación
  const getNeighborhoodId = useCallback(async (lat: number, lng: number) => {
    if (!map.current) return null;

    try {
      // Obtener el token de Mapbox
      const token = process.env.REACT_APP_MAPBOX_TOKEN;
      if (!token) {
        console.error('No se encontró el token de Mapbox');
        return null;
      }

      // Crear un bounding box alrededor del punto
      const radius = 0.01; // Aproximadamente 1km
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const _bounds = [
        lng - radius,  // oeste
        lat - radius,  // sur
        lng + radius,  // este
        lat + radius   // norte
      ];

      // Construir la URL para obtener las features
      const tilesetId = LAYER_CONFIG.neighborhoods.tilesetId.replace('mapbox://', '');
      const url = `https://api.mapbox.com/v4/${tilesetId}/tilequery/${lng},${lat}.json?radius=1000&limit=5&access_token=${token}`;

      // Hacer la llamada a la API
      const response = await fetch(url);
      const data = await response.json();

      if (data.features && data.features.length > 0) {
        // Ordenar por distancia al punto
        const sortedFeatures = data.features.sort((a: any, b: any) => 
          (a.properties.tilequery.distance - b.properties.tilequery.distance)
        );

        const closestFeature = sortedFeatures[0];
        const neighborhoodId = closestFeature.properties?.id;
        
        if (neighborhoodId) {
          return neighborhoodId;
        }
      }
      return null;
    } catch (error) {
      console.error('Error al obtener neighborhood:', error);
      return null;
    }
  }, []);

  // Función auxiliar para determinar si un punto está dentro de un polígono
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isPointInPolygon = (point: [number, number], polygon: number[][]) => {
    const [x, y] = point;
    let inside = false;
    
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [xi, yi] = polygon[i];
      const [xj, yj] = polygon[j];
      
      const intersect = ((yi > y) !== (yj > y)) && 
        (x < ((xj - xi) * (y - yi) / (yj - yi) + xi));
      
      if (intersect) {
        inside = !inside;
      }
    }
    
    return inside;
  };

  // Función auxiliar para validar coordenadas
  const isValidCoordinate = (lng: number, lat: number): boolean => {
    return !isNaN(lng) && !isNaN(lat) && 
           isFinite(lng) && isFinite(lat) && 
           lng >= -180 && lng <= 180 && 
           lat >= -90 && lat <= 90;
  };

  // Función para manejar la geolocalización
  const handleGeolocation = useCallback(async () => {
    if (!navigator.geolocation) {
      console.error('Geolocalización no está soportada en este navegador');
      setIsLoading(false);
      return;
    }

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      const { latitude: lat, longitude: lng } = position.coords;
      const inNYC = isInNYC(lat, lng);

      if (!inNYC) {
        setUserLocation({
          lat,
          lng,
          neighborhoodId: null,
          isInNYC: false
        });
        setIsLoading(false);
        return;
      }

      // Esperar a que el mapa esté completamente cargado
      if (!map.current?.isStyleLoaded()) {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }

      const neighborhoodId = await getNeighborhoodId(lat, lng);
      
      setUserLocation({
        lat,
        lng,
        neighborhoodId: neighborhoodId,
        isInNYC: true
      });

      // Centrar el mapa directamente en la ubicación del usuario
      if (map.current) {
        map.current.setCenter([lng, lat]);
        map.current.setZoom(12);
        
        // Esperar un momento para que el mapa se centre y haga zoom
        await new Promise(resolve => setTimeout(resolve, 1000));
        setIsLoading(false);
      } else {
        console.warn('No se puede centrar el mapa con coordenadas inválidas:', { lng, lat });
        setIsLoading(false);
      }

    } catch (error) {
      console.error('Error al obtener la ubicación:', error);
      setIsLoading(false);
    }
  }, [isInNYC, getNeighborhoodId]);

  // Función para manejar los votos
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleVote = useCallback(async (isPositive: boolean) => {
    if (!selectedArea) return;

    try {
      const updatedStats = await api.vote(selectedArea.id, isPositive);
      setSelectedArea(updatedStats);
      
      // Guardar información del último voto
      setLastVote({
        isPositive,
        timestamp: new Date().toISOString(),
        neighborhood: selectedArea.name,
        color: selectedArea.color || '#E1D4C3'
      });
      
      // Actualizar datos de todas las áreas
      await loadAllAreasData();
    } catch (error) {
      console.error('Error al votar:', error);
    }
  }, [selectedArea, loadAllAreasData]);

  // Mantener la ubicación del usuario en un ref para callbacks
  useEffect(() => {
    userLocationRef.current = userLocation;
  }, [userLocation]);

  useEffect(() => {
    areaDataRef.current = areaData;
  }, [areaData]);

  // Función para crear el contenido HTML del tooltip
  const createTooltipContent = useCallback((area: AreaStats, isUserHere: boolean = false) => {
    const hasVotes = area.total_votes > 0;
    const positivePercentage = hasVotes ? ((area.positive_votes / area.total_votes) * 100).toFixed(1) : '0.0';
    const negativePercentage = hasVotes ? ((area.negative_votes / area.total_votes) * 100).toFixed(1) : '0.0';
    const emotionSvg = getEmotionIcon(Number(positivePercentage), area.total_votes);
    const showReliabilityNote = area.confidence_percentage < 95;
    const asterisk = showReliabilityNote ? '*' : '';

    if (!hasVotes) {
      return `
        <div class="tooltip-content" style="min-width: 220px; padding: 12px;">
          <div style="display: flex; align-items: center;">
            <img src="${emotionSvg}" style="width: 28px; height: 28px; margin-right: 10px;" />
            <div>
              <strong style="font-size: 15px; color: #FFFFFF;">${area.name}</strong>
              ${isUserHere ? `<div style="font-size: 12px; color: #2196F3; margin-top: -2px;">${t('tooltip.you_are_here', 'You are here!')}</div>` : ''}
              <div style="font-size: 13px; color: #FFFFFF; margin-top: 4px;">${t('tooltip.no_votes_yet', 'No votes yet')}</div>
            </div>
          </div>
        </div>
      `;
    }

    return `
      <div class="tooltip-content" style="min-width: 220px; padding: 12px;">
        <div style="display: flex; align-items: center; margin-bottom: 12px;">
          <img src="${emotionSvg}" style="width: 28px; height: 28px; margin-right: 10px;" />
          <div>
            <strong style="font-size: 15px; color: #FFFFFF;">${area.name}</strong>
            ${isUserHere ? `<div style="font-size: 12px; color: #2196F3; margin-top: -2px;">${t('tooltip.you_are_here', 'You are here!')}</div>` : ''}
          </div>
        </div>
        
        <!-- Barra Good -->
        <div style="margin-bottom: 10px;">
          <div style="margin-bottom: 4px;">
            <span style="font-weight: bold; color: #28A738; font-size: 13px;">${t('voting.good')}</span>
            <span style="color: #28A738; font-size: 13px;"> ${positivePercentage}%${asterisk}</span>
          </div>
          <div style="background-color: #f0f0f0; height: 8px; border-radius: 4px; overflow: hidden;">
            <div style="background-color: #28A738; height: 100%; width: ${positivePercentage}%; border-radius: 4px;"></div>
          </div>
        </div>
        
        <!-- Barra Bad -->
        <div style="margin-bottom: ${showReliabilityNote ? '8px' : '0'};">
          <div style="margin-bottom: 4px;">
            <span style="font-weight: bold; color: #E40714; font-size: 13px;">${t('voting.bad')}</span>
            <span style="color: #E40714; font-size: 13px;"> ${negativePercentage}%${asterisk}</span>
          </div>
          <div style="background-color: #f0f0f0; height: 8px; border-radius: 4px; overflow: hidden;">
            <div style="background-color: #E40714; height: 100%; width: ${negativePercentage}%; border-radius: 4px;"></div>
          </div>
        </div>
        
        ${showReliabilityNote ? `
        <div style="font-size: 10px; color: #999; margin-top: 8px; font-style: italic;">
          ${t('tooltip.low_confidence')}
        </div>
        ` : ''}
      </div>
    `;
  }, [getEmotionIcon, t]);

  // Función para manejar el hover sobre las áreas
  const handleAreaHover = useCallback((e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => {
    const currentAreaData = areaDataRef.current;
    if (!map.current || !e.features?.length) return;

    const feature = e.features[0];
    const areaId = feature.properties?.id;
    const currentZoom = map.current.getZoom();
    
    // Si estamos en el nivel de zoom de ciudad, siempre mostrar stats de toda la ciudad
    if (currentZoom < ZOOM_LEVELS.boroughs.min) {
      const cityStats = getCityStats();
      if (!cityStats) return;
      
      // Crear o actualizar el popup
      if (!popup.current) {
        popup.current = new mapboxgl.Popup(getAreaPopupOptions());
      }
      
      // Actualizar la posición y contenido del popup
      popup.current
        .setLngLat(e.lngLat)
        .setHTML(createTooltipContent(cityStats))
        .addTo(map.current);
        
      return;
    }
    
    const areaStats = currentAreaData[areaId];

    if (!areaStats) return;

    // Crear o actualizar el popup
    if (!popup.current) {
      popup.current = new mapboxgl.Popup(getAreaPopupOptions());
    }

    const userNeighborhoodId = userLocationRef.current?.neighborhoodId || null;
    const userNeighborhood = userNeighborhoodId ? currentAreaData[userNeighborhoodId] : null;
    const normalize = (value: unknown) =>
      value === null || value === undefined ? '' : String(value).trim().toLowerCase();
    const isUserHere = Boolean(
      userNeighborhoodId !== null &&
        (normalize(feature.properties?.id) === normalize(userNeighborhoodId) ||
          normalize(areaStats.id) === normalize(userNeighborhoodId) ||
          (userNeighborhood?.name &&
            (normalize(areaStats.name) === normalize(userNeighborhood.name) ||
              normalize(feature.properties?.name) === normalize(userNeighborhood.name))))
    );

    // Actualizar la posición y contenido del popup
    popup.current
      .setLngLat(e.lngLat)
      .setHTML(createTooltipContent(areaStats, isUserHere))
      .addTo(map.current);
  }, [createTooltipContent, getAreaPopupOptions, getCityStats]);

  // Función para manejar cuando el mouse sale del área
  const handleAreaLeave = useCallback(() => {
    if (popup.current) {
      popup.current.remove();
    }
  }, []);

  // Inicializar el mapa una sola vez por montaje.
  // Evitamos recrearlo por cambios menores en props para eliminar parpadeos/pérdida de capas.
  useEffect(() => {
    let mapInstance: mapboxgl.Map | null = null;
    const emotionMarkersRef = emotionMarkers.current;
    const labelMarkersRef = labelMarkers.current;

    const initializeMap = async () => {
      if (mapInitializedRef.current || !mapContainer.current) return;

      const initialCenter = initialLocation
        ? [initialLocation.longitude, initialLocation.latitude]
        : [-73.9712, 40.7831];
      const initialZoom = initialLocation ? 12 : 10;

      mapInstance = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/dark-v11', // Cambiar de light-v11 a dark-v11
        center: initialCenter as [number, number],
        zoom: initialZoom,
        minZoom: 0,
        maxZoom: 22,
        maxTileCacheSize: 100
      });

      map.current = mapInstance;

      mapInstance.on('load', async () => {
        // Configurar el color del agua y el fondo
        if (mapInstance) {
          try {
            // Intentar cambiar solo las capas de agua que existen
            const waterLayers = ['water', 'waterway'];
            waterLayers.forEach(layerName => {
              try {
                if (mapInstance && mapInstance.getLayer(layerName)) {
                  mapInstance.setPaintProperty(layerName, 'fill-color', '#141414');
                }
              } catch(e) { /* Layer might not exist */ }
            });
            
            // Remove all non-data fill layers to ensure a uniform background color
            const style = mapInstance.getStyle();
            if (style && style.layers) {
              const appLayerIds = new Set(Object.keys(LAYER_CONFIG).flatMap(l => [`${l}-fill`, `${l}-outline`]));

              // Create a copy of the layers array to iterate over, as we're modifying the map's layers
              [...style.layers].forEach(layer => {
                if (mapInstance && layer.type === 'fill' && !appLayerIds.has(layer.id) && !layer.id.includes('water')) {
                  if (mapInstance.getLayer(layer.id)) {
                    mapInstance.removeLayer(layer.id);
                  }
                }
              });
            }

          } catch(e) {
            console.warn(`Error setting base map colors: ${e}`);
          }
          enhanceBaseMap(mapInstance);
        }
        
        // Esperar a que el estilo esté completamente cargado
        while (!mapInstance?.isStyleLoaded()) {
          await new Promise(r => setTimeout(r, 100));
        }
        
        initializeLayers();
        
        // Esperar a que la capa inicial esté disponible
        while (!mapInstance?.isStyleLoaded() || !mapInstance?.getLayer('boroughs-fill')) {
          await new Promise(r => setTimeout(r, 100));
        }

        // Mover las etiquetas de nombres de áreas por encima de nuestras capas
        moveAreaLabelsAboveOurLayers(mapInstance);

        mapInitializedRef.current = true;
        
        // Cargar datos iniciales y estadísticas globales
        await loadAllAreasData();

        // Configurar eventos
        mapInstance?.on('zoom', handleZoom);
        mapInstance?.on('click', handleAreaHover);
        mapInstance?.on('mousemove', handleAreaHover);
        mapInstance?.on('mouseleave', handleAreaLeave);
        
        // Forzar estilos de calles después de la carga completa
        setTimeout(() => {
          if (mapInstance && map.current === mapInstance && mapInstance.isStyleLoaded()) {
            enhanceBaseMap(mapInstance);
          }
        }, 1000);

        // Actualizar la capa inicial
        const zoom = mapInstance.getZoom();
        updateLayerVisibility(zoom);

        // Si ya tenemos ubicación precargada, evitar geolocalizar de nuevo.
        if (initialLocation) {
          setUserLocation({
            lat: initialLocation.latitude,
            lng: initialLocation.longitude,
            neighborhoodId: initialNeighborhoodId,
            isInNYC: !isOutsideNYCView
          });
        } else {
          await handleGeolocation();
        }

        // Añadir event listeners para hover
        ['city-fill', 'boroughs-fill', 'neighborhoods-fill'].forEach(layerId => {
          mapInstance?.on('mouseenter', layerId, (e) => handleAreaHover(e));
          mapInstance?.on('mousemove', layerId, (e) => handleAreaHover(e));
          mapInstance?.on('mouseleave', layerId, handleAreaLeave);
        });

        // Añadir event listener para click en la capa de ciudad
        mapInstance?.on('click', 'city-fill', (e) => {
          if (!map.current || !e.features?.length) return;
          
          const cityStats = getCityStats();
          if (!cityStats) return;
          
          setSelectedArea(cityStats);
        });

        setIsLoading(false);
      });
    };

    // Limpiar estado previo si existe
    if (map.current) {
      Object.values(emotionMarkers.current).forEach(marker => marker.remove());
      emotionMarkers.current = {};
      emotionMarkerMeta.current = {};
      layerStyleSignatureRef.current = {};
      lastAreaDataSignatureRef.current = '';
      map.current.remove();
      map.current = null;
      mapInitializedRef.current = false;
    }

    initializeMap().catch(error => {
      console.error('Error durante la inicialización del mapa:', error);
    });

    return () => {
      if (zoomTimeout.current) {
        clearTimeout(zoomTimeout.current);
      }
      if (popup.current) {
        popup.current.remove();
      }
      if (mapInstance?.getLayer('user-neighborhood-outline')) {
        mapInstance.removeLayer('user-neighborhood-outline');
      }
      Object.values(emotionMarkersRef).forEach(marker => marker.remove());
      Object.values(labelMarkersRef).forEach(marker => marker.remove());
      if (mapInstance) {
        mapInstance.remove();
        map.current = null;
        mapInitializedRef.current = false;
        emotionMarkerMeta.current = {};
        layerStyleSignatureRef.current = {};
        lastAreaDataSignatureRef.current = '';
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Añadir un efecto para actualizar el borde azul del usuario (neighborhood)
  useEffect(() => {
    if (!map.current || !userLocation?.neighborhoodId) return;

    const neighborhoodId = userLocation.neighborhoodId;

    const addOutlines = () => {
      if (!map.current) return;

      // Esperar a que el estilo y las fuentes estén listos
      if (!map.current.isStyleLoaded()) return;
      if (!map.current.getSource('neighborhoods-source')) return;

      const addOutlineLayer = (
        layerId: string,
        sourceId: string,
        sourceLayer: string,
        filterId: string | null
      ) => {
        if (!map.current) return;
        if (!filterId) {
          if (map.current.getLayer(layerId)) {
            map.current.removeLayer(layerId);
          }
          return;
        }
        if (map.current.getLayer(layerId)) {
          // Evitar remove/add para no provocar parpadeo ni pérdida del borde.
          map.current.setFilter(layerId, ['==', ['get', 'id'], filterId]);
          return;
        }
        map.current.addLayer({
          id: layerId,
          type: 'line',
          source: sourceId,
          'source-layer': sourceLayer,
          paint: {
            'line-color': '#2196F3',
            'line-width': 3,
            'line-opacity': 1
          },
          filter: ['==', ['get', 'id'], filterId]
        });
      };

      addOutlineLayer(
        'user-neighborhood-outline',
        'neighborhoods-source',
        LAYER_CONFIG.neighborhoods.sourceLayer,
        neighborhoodId
      );
    };

    addOutlines();

    if (
      map.current &&
      (!map.current.isStyleLoaded() ||
        !map.current.getSource('neighborhoods-source'))
    ) {
      map.current.once('idle', addOutlines);
    }
  }, [userLocation?.neighborhoodId]);

  // Si el estilo se recarga (p. ej. tras tab suspendida), recrea nuestras capas y estilos.
  useEffect(() => {
    if (!map.current) return;

    const mapInstance = map.current;

    const scheduleIntegrityCheck = () => {
      if (mapIntegrityRafRef.current !== null) {
        cancelAnimationFrame(mapIntegrityRafRef.current);
      }
      mapIntegrityRafRef.current = requestAnimationFrame(() => {
        mapIntegrityRafRef.current = null;
        if (!map.current || !map.current.isStyleLoaded()) return;

        const hasCoreLayers =
          Boolean(map.current.getLayer('city-fill')) &&
          Boolean(map.current.getLayer('boroughs-fill')) &&
          Boolean(map.current.getLayer('neighborhoods-fill'));

        if (!hasCoreLayers) {
          layerStyleSignatureRef.current = {};
          ensureMapLayersAndStyles();
          loadAllAreasData();
        }
      });
    };

    mapInstance.on('styledata', scheduleIntegrityCheck);
    mapInstance.on('webglcontextrestored', scheduleIntegrityCheck);

    return () => {
      mapInstance.off('styledata', scheduleIntegrityCheck);
      mapInstance.off('webglcontextrestored', scheduleIntegrityCheck);
      if (mapIntegrityRafRef.current !== null) {
        cancelAnimationFrame(mapIntegrityRafRef.current);
        mapIntegrityRafRef.current = null;
      }
    };
  }, [ensureMapLayersAndStyles, loadAllAreasData]);

  // Refresco suave de datos cada 30s sin reinstanciar Mapbox.
  useEffect(() => {
    const ACTIVE_INTERVAL_MS = 15000;
    const IDLE_INTERVAL_MS = 30000;
    const IDLE_THRESHOLD_MS = 60000;
    let timer: number | null = null;
    let inFlight = false;
    let failures = 0;
    let lastInteractionAt = Date.now();

    const jitterDelay = (baseMs: number) => {
      const jitter = Math.round(baseMs * 0.1 * (Math.random() * 2 - 1));
      return Math.max(1000, baseMs + jitter);
    };

    const backoffMs = () => {
      if (failures <= 0) return 0;
      if (failures === 1) return 10000;
      if (failures === 2) return 20000;
      return 60000;
    };

    const computeBaseInterval = () => {
      if (document.hidden) return;
      const idleForMs = Date.now() - lastInteractionAt;
      return idleForMs <= IDLE_THRESHOLD_MS ? ACTIVE_INTERVAL_MS : IDLE_INTERVAL_MS;
    };

    const clearTimer = () => {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    };

    const scheduleNext = (baseMs?: number) => {
      clearTimer();
      if (document.hidden) return;
      const interval = baseMs ?? computeBaseInterval();
      if (!interval) return;
      timer = window.setTimeout(runPoll, jitterDelay(interval + backoffMs()));
    };

    const refreshIfVisible = () => {
      if (document.hidden) return false;
      if (!map.current || !mapInitializedRef.current) return false;
      // Asegura que el canvas se recomponga al volver de background.
      map.current.resize();
      ensureMapLayersAndStyles();
      loadAllAreasData();
      return true;
    };

    const runPoll = () => {
      if (document.hidden) {
        clearTimer();
        return;
      }
      if (inFlight) {
        scheduleNext(3000);
        return;
      }
      inFlight = true;
      try {
        const ok = refreshIfVisible();
        failures = ok ? 0 : Math.min(failures + 1, 3);
      } catch {
        failures = Math.min(failures + 1, 3);
      } finally {
        inFlight = false;
      }
      scheduleNext();
    };

    const markInteraction = () => {
      lastInteractionAt = Date.now();
    };

    const onVisibilityOrFocus = () => {
      if (document.hidden) {
        clearTimer();
        return;
      }
      markInteraction();
      const ok = refreshIfVisible();
      failures = ok ? 0 : Math.min(failures + 1, 3);
      scheduleNext(ACTIVE_INTERVAL_MS);
    };

    const interactionEvents: Array<keyof WindowEventMap> = ['pointerdown', 'touchstart', 'keydown', 'scroll', 'focus'];
    interactionEvents.forEach(eventName => {
      window.addEventListener(eventName, markInteraction, { passive: true });
    });
    document.addEventListener('visibilitychange', onVisibilityOrFocus);
    window.addEventListener('focus', onVisibilityOrFocus);
    onVisibilityOrFocus();

    return () => {
      clearTimer();
      interactionEvents.forEach(eventName => {
        window.removeEventListener(eventName, markInteraction);
      });
      document.removeEventListener('visibilitychange', onVisibilityOrFocus);
      window.removeEventListener('focus', onVisibilityOrFocus);
    };
  }, [ensureMapLayersAndStyles, loadAllAreasData]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100vh' }}>
      {/* DynamicFavicon se maneja en VotingScreen con datos actuales */}
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#1a1a1a', // Cambiar de '#e5d8c6' a '#1a1a1a'
            zIndex: 1000,
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}
      <Box
        className="map-canvas-container"
        ref={mapContainer}
        sx={{
          height: '100%',
          width: '100%',
          visibility: isLoading ? 'hidden' : 'visible',
          pointerEvents: 'auto',
          '& .mapboxgl-canvas': {
            mixBlendMode: 'multiply',
          }
        }}
      />
      
      {/* Footer con enlaces */}
      <div className="map-footer">
        <p className="footer-text">
          {t('footer.made_under')} · <a href={currentLanguage === 'en' ? '/about.html' : `/about-${currentLanguage}.html`} className="footer-link">{t('footer.about')}</a> · <a href="https://shop.hwa.nyc/" className="footer-link" target="_blank" rel="noopener noreferrer">{t('footer.merch')}</a> · <a href={currentLanguage === 'en' ? '/privacy.html' : `/privacy-${currentLanguage}.html`} className="footer-link">{t('footer.privacy')}</a> · <a href={currentLanguage === 'en' ? '/terms.html' : `/terms-${currentLanguage}.html`} className="footer-link">{t('footer.terms')}</a> · <a href={currentLanguage === 'en' ? '/support.html' : `/support-${currentLanguage}.html`} className="footer-link">{t('footer.support', 'Support')}</a> · <a href="https://hwa.today" className="footer-link" target="_blank" rel="noopener noreferrer">hwa.today</a>
        </p>
        <div className="footer-social-links" aria-label="Social links">
          <a href="https://www.instagram.com/hwa.nyc/" className="footer-social-link" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
            <img src={instagramIcon} alt="" />
          </a>
          <a href="https://www.tiktok.com/@hwa...nyc" className="footer-social-link" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
            <img src={tiktokIcon} alt="" />
          </a>
          <a href="https://bsky.app/profile/hwa.nyc" className="footer-social-link" target="_blank" rel="noopener noreferrer" aria-label="Bluesky">
            <img src={blueskyIcon} alt="" />
          </a>
        </div>
        <LanguageSelector className="footer-language-selector" />
      </div>
    </Box>
  );
};

export default Map; 